/**
 * URL-based navigation: build instance/screen URLs, push/replace history, and
 * bridge sidebar/iframe edge cases.
 *
 * Routes through `expo-router` for actual navigation but does its own URL
 * construction so the path shape (`/[silo]/[structure]/[instance]/[screen?]`)
 * and the `_g` global-param suffix stay consistent across web and native. Two
 * non-obvious behaviors: when running inside an iframe, history `pushState` is
 * rebound to `replaceState` (and `go` to a no-op) so the host page's back
 * button doesn't trap inside the embed; when the current path is a teaser or
 * overlay, `gotoUrl` posts a `psi-sidebar-gotoUrl` message to the parent
 * instead of navigating locally so the sidebar opens with the target URL.
 * `closeSidebar` similarly bridges to the parent over `postMessage` and to
 * native shells via `requestAppClosePanel`.
 */

import { type Href, router, type UnknownInputParams, useGlobalSearchParams } from 'expo-router';
import { getIsInPsiNativeAppWebview } from 'platform-specific/url';
import { useMemo } from 'react';
import { Platform } from 'react-native';
import type { Datastore } from 'util/datastore';
import { clearGlobalSidebarTimers } from 'util/timers';

import { getDeploymentConfig, getIsInIFrame, parsePsiPath, stripSuffix } from './util';
import { requestAppClosePanel } from './webview';

export function closeSidebar(datastore: Datastore) {
    clearGlobalSidebarTimers(datastore);
    if (window.parent) {
        window.parent.postMessage('psi-close-sidebar', '*');
    }
    if (getIsInPsiNativeAppWebview()) {
        requestAppClosePanel();
    }
}

export function makeQueryForParams(index: number, params: Record<string, string> = {}, query = new URLSearchParams()) {
    for (const [key, value] of Object.entries(params ?? {})) {
        if (value) {
            query.set(key + getParamSuffixForIndex(index), value);
        }
    }
    return query;
}

export function addGlobalParamsToQuery(globalParams: Record<string, string>, query: URLSearchParams) {
    for (const [key, value] of Object.entries(globalParams ?? {})) {
        query.set(key + '_g', value);
    }
}

export function getGlobalParams(url = window.location.href): Record<string, string> {
    const params: Record<string, string | string[]> = {};
    const urlSearchParams = new URL(url).searchParams;
    for (const key of urlSearchParams.keys()) {
        params[key] = urlSearchParams.getAll(key);
    }
    return getParamsWithSuffix(params, '_g');
}

/**
 * The global (`_g`) params on the current route, with their suffix kept so they
 * spread into an href's params.
 */
export function useGlobalHrefParams(): UnknownInputParams {
    const params = useGlobalSearchParams();
    return useMemo(() => Object.fromEntries(Object.entries(params).filter(([key]) => key.endsWith('_g'))), [params]);
}

export function internal_gotoInstance({
    structureKey,
    instanceKey,
    siloKey = undefined,
    params = {},
    globalParams = {},
    replace = false,
}: {
    structureKey: string;
    instanceKey: string;
    siloKey?: string;
    params?: Record<string, string>;
    globalParams?: Record<string, string>;
    replace?: boolean;
}) {
    const url = makeUrlForInstance({ structureKey, instanceKey, siloKey, params, globalParams });
    if (replace) {
        replaceUrl(url);
    } else {
        gotoUrl(url);
    }
}

export function internal_gotoInstanceScreen({
    structureKey,
    instanceKey,
    screenKey,
    params = {},
    globalParams = {},
    replace = false,
    withAnchor = false,
}: {
    structureKey: string;
    instanceKey: string;
    screenKey: string;
    params?: Record<string, string>;
    globalParams?: Record<string, string>;
    replace?: boolean;
    withAnchor?: boolean;
}) {
    const parts = [structureKey, instanceKey, screenKey];
    const query = makeQueryForParams(1, params);
    addGlobalParamsToQuery(globalParams, query);
    addGlobalParamsToQuery(getGlobalParams(), query);
    if (replace) {
        replaceUrl(makeUrl(parts, query));
    } else {
        gotoUrl(makeUrl(parts, query), withAnchor ? { withAnchor: true } : undefined);
    }
}

export function getParamsWithSuffix(params: Record<string, string | string[] | undefined>, suffix: string) {
    const result: Record<string, string> = {};
    for (const [key, values] of Object.entries(params)) {
        const value = Array.isArray(values) ? values.at(-1) : values;
        if (value === undefined) {
            continue;
        }
        if (suffix) {
            if (key.endsWith(suffix)) {
                result[stripSuffix(key, suffix)] = value;
            }
        } else {
            if (!key.includes('_')) {
                result[key] = value;
            }
        }
    }
    return result;
}

export interface MakeUrlForInstanceParams {
    structureKey: string;
    instanceKey: string;
    screenKey?: string;
    params?: Record<string, string>;
    globalParams?: Record<string, string>;
    siloKey?: string;
}

export function makeUrlForInstance({
    structureKey,
    instanceKey,
    screenKey,
    params = {},
    globalParams = {},
    siloKey = undefined,
}: MakeUrlForInstanceParams) {
    const parts = [structureKey, instanceKey];
    if (screenKey) parts.push(screenKey);
    const query = makeQueryForParams(0, params);
    addGlobalParamsToQuery(getGlobalParams(), query);
    addGlobalParamsToQuery(globalParams, query);
    return makeUrl(parts, query, siloKey);
}

// Like makeUrlForInstance but origin-relative (basePath + path, no
// scheme/host), suitable for an anchor href that should stay within the app
// while still carrying the deployment basePath.
export function makeRelativeUrlForInstance(args: MakeUrlForInstanceParams) {
    return makeRelative(makeUrlForInstance(args));
}

export function makeUrl(parts: string[], query = new URLSearchParams(), siloKey?: string) {
    const url = new URL(window.location.href);
    const resolvedSiloKey = siloKey ?? parsePsiPath(url.pathname).siloKey ?? 'global';

    url.pathname = `${getDeploymentConfig().basePath ?? '/'}${resolvedSiloKey}/${parts.join('/')}`;
    url.search = query.toString();
    return url.toString();
}

function makeRelative(url: string) {
    if (Platform.OS === 'web' && url.startsWith(window.location.origin)) {
        url = url.slice(window.location.origin.length);
    }

    return url;
}

function getParamSuffixForIndex(index: number) {
    return index ? '' + index : '';
}

export function closeWindow() {
    window.close();
}

if (getIsInIFrame(window)) {
    // Browser back buttons usually consider iframe and top-level navigations.
    // Therefore, patch router behavior in an iframe in order to be unobtrusive.
    // Expo Router only uses `pushState`, or `go` followed by `replaceState`.
    window.history.pushState = window.history.replaceState;
    window.history.go = () => {};

    // psi-sidebar.js sends this when reopening the sidebar so the iframe
    // navigates without a full reload and without pushing a history entry.
    window.addEventListener('message', (event) => {
        if (event.source === window.parent && event?.data?.type === 'psi-navigate-replace') {
            const url = event.data.url;
            if (typeof url === 'string') {
                replaceUrl(url);
            }
        }
    });
}

export function goBack() {
    router.back();
}

export function canGoBack() {
    return router.canGoBack();
}

export function gotoUrl(url: string, options?: { withAnchor?: boolean }) {
    if (window.location.pathname.includes('/teaser') || window.location.pathname.includes('/overlayScreen')) {
        // Do not navigate from the teaser or overlay. Ask the host page to load
        // the URL in the sidebar.
        window.parent.postMessage({ type: 'psi-sidebar-gotoUrl', url }, '*');
    } else {
        // Expo Router needs relative URLs or it does full page reloads on
        // navigation.
        url = makeRelative(url);
        router.push(url as Href, options);
    }
}

/** Load the app afresh at `url`, replacing the current history entry. */
export function reloadAtUrl(url: string) {
    window.location.replace(url);
}

export function replaceUrl(url: string) {
    // Expo Router needs relative URLs or it does full page reloads on
    // navigation.
    url = makeRelative(url);
    router.replace(url as Href);
}
